import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import usePropertyContent from '@/hooks/usePropertyContent';
import updateRouteQuery from '@/utils/updateRouteQuery';
import validateSearchQuery from '@/utils/validateSearchQuery';
import useDestination from '../../../../hooks/useDestination';
import useOnSearch from '../../../../hooks/useOnSearch';
import BFF from '../../../../services/BFF';
import getSearchQuery from '../../../../utils/getSearchQuery';
import GetPackageOffersContext from './GetPackageOffersContext';

/**
 * @param {{ children: React.ReactNode }} props
 */
const GetPackageOffersProvider = ({ children }) => {
  const propertyContent = usePropertyContent();
  const destination = useDestination();

  const [state, setState] = useState({});
  const [searchQuery, setSearchQuery] = useState();

  const getPackagesOffers = useCallback(
    async (searchParams) => {
      if (!searchParams) {
        setSearchQuery();
        return;
      }

      if (!validateSearchQuery(destination.searchConfig, searchParams)) {
        setSearchQuery();
        updateRouteQuery(getSearchQuery({}), true);
        return;
      }

      const params = {
        ...searchParams,
        propertyId: propertyContent.id,
        ...destination.searchConfig,
      };

      setSearchQuery(getSearchQuery(params));

      const data = await BFF.getPackageOffers()(params);

      setState(data);
    },
    [destination, propertyContent],
  );

  const { error, loading } = useOnSearch(getPackagesOffers);

  useEffect(() => {
    if (error) {
      setState({});
    }
  }, [error]);

  const { flightOptions, packageOffers, availability } = state;
  const value = useMemo(
    () => ({
      error,
      availability,
      loading,
      flightOptions,
      packageOffers,
      searchQuery,
    }),
    [availability, error, flightOptions, loading, packageOffers, searchQuery],
  );

  return (
    <GetPackageOffersContext.Provider value={value}>
      {children}
    </GetPackageOffersContext.Provider>
  );
};

GetPackageOffersProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default GetPackageOffersProvider;
